import { useMemo, useState } from "react";
import { MonthGrid } from "@/components/MonthGrid";
import { addMonths } from "@/lib/calendar";
import { recordingStatus } from "@/components/scout/RecordingPanel";
import { CalendarDays, Save } from "lucide-react";
import { toast } from "sonner";
import { patchCandidate } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
      })
    : "Not scheduled";

const EpisodeHeader = ({ c, when, setWhen }) => (
  <div className="flex flex-wrap items-start justify-between gap-4">
    <div>
      <h3 className="font-display text-xl text-[#0A192F] tracking-tight">{c.name}</h3>
      <p className="text-sm text-slate-600">{c.role}{c.affiliation ? ` · ${c.affiliation}` : ""}</p>
      <p data-testid="scout-episode-when" className="mt-1 text-xs text-[var(--accent)]">{recordingStatus(c.recording_at).text}</p>
    </div>
    <label className="text-xs text-slate-500">
      Recording date
      <input
        type="datetime-local"
        data-testid="scout-episode-date"
        value={when}
        onChange={(e) => setWhen(e.target.value)}
        className="mt-1 block border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] outline-none focus:border-[var(--accent)]"
      />
    </label>
  </div>
);

const EpisodeRow = ({ c, onChange }) => {
  const [when, setWhen] = useState(c.recording_at ? c.recording_at.slice(0, 16) : "");
  const [prep, setPrep] = useState(c.prep_notes || "");
  const [busy, setBusy] = useState(false);
  const dirty = when !== (c.recording_at ? c.recording_at.slice(0, 16) : "") || prep !== (c.prep_notes || "");

  const save = async () => {
    setBusy(true);
    try {
      onChange(await patchCandidate(c.id, { recording_at: when || "", prep_notes: prep }));
      toast.success("Episode saved");
    } catch (err) {
      toast.error(extractApiError(err, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article id={`scout-episode-${c.id}`} data-testid={`scout-episode-${c.id}`} className="border-t border-slate-200 pt-5 pb-3 scroll-mt-24">
      <EpisodeHeader c={c} when={when} setWhen={setWhen} />
      <textarea
        data-testid="scout-episode-prep"
        rows={3}
        value={prep}
        onChange={(e) => setPrep(e.target.value)}
        placeholder="Prep notes — angles to press, things they've said, logistics, links…"
        className="mt-3 w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--accent)] resize-y"
      />
      <button
        type="button"
        data-testid="scout-episode-save"
        onClick={save}
        disabled={!dirty || busy}
        className="mt-2 inline-flex items-center gap-2 border border-[#0A192F] hover:bg-[#0A192F] hover:text-white disabled:opacity-40 px-3 py-2 text-xs font-medium transition-colors"
      >
        <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
      </button>
    </article>
  );
};

export const EpisodeCalendar = ({ candidates, onChange }) => {
  const booked = useMemo(() => candidates.filter((c) => c.status === "booked"), [candidates]);
  const [month, setMonth] = useState(new Date());
  const events = useMemo(
    () => booked.filter((c) => c.recording_at).map((c) => ({ ...c, at: new Date(c.recording_at.slice(0, 16)) })),
    [booked]
  );
  const now = Date.now();
  const byDate = (a, b) => new Date(a.recording_at) - new Date(b.recording_at);
  const upcoming = booked.filter((c) => c.recording_at && new Date(c.recording_at) >= now).sort(byDate);
  const unscheduled = booked.filter((c) => !c.recording_at);
  const past = booked.filter((c) => c.recording_at && new Date(c.recording_at) < now).sort(byDate).reverse();

  const Group = ({ title, list, testid }) =>
    list.length ? (
      <div data-testid={testid} className="mb-8">
        <div className="text-xs tracking-tight text-slate-500 mb-2">{title} · {list.length}</div>
        {list.map((c) => <EpisodeRow key={c.id} c={c} onChange={onChange} />)}
      </div>
    ) : null;

  return (
    <section data-testid="scout-calendar">
      <div className="text-xs tracking-tight text-[var(--accent)] mb-2 flex items-center gap-2">
        <CalendarDays className="w-3.5 h-3.5" /> Episode Calendar · {booked.length} booked
      </div>
      <p data-testid="scout-calendar-reminder-note" className="text-xs text-slate-500 mb-6">
        A prep sheet (notes, questions, sources, contact) is emailed to you at 7:00 AM ET the day before each scheduled recording.
      </p>
      {booked.length === 0 ? (
        <p data-testid="scout-calendar-empty" className="text-sm text-slate-500 border-t border-slate-200 pt-8">
          No booked guests yet. Set a candidate&apos;s status to <strong>booked</strong> and they&apos;ll appear here.
        </p>
      ) : (
        <>
          <div className="mb-10">
            <MonthGrid
              month={month}
              onMonth={(n) => setMonth((m) => addMonths(m, n))}
              testId="scout-month-grid"
              events={events}
              renderEvent={(ev) => (
                <a key={ev.id} href={`#scout-episode-${ev.id}`} data-testid={`scout-month-event-${ev.id}`}
                  className={`block text-[10px] md:text-xs leading-tight px-1.5 py-1 truncate text-white ${ev.hide_from_site ? "bg-slate-400" : "bg-[var(--accent)]"} hover:bg-[#0A192F] transition-colors`}
                  title={`${ev.name} · ${fmt(ev.recording_at)}${ev.hide_from_site ? " (hidden from site)" : ""}`}>
                  {new Date(ev.recording_at.slice(0, 16)).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })} · {ev.name.split(" ").slice(-1)[0]}
                </a>
              )}
            />
          </div>
          <Group title="Upcoming" list={upcoming} testid="scout-calendar-upcoming" />
          <Group title="Needs a date" list={unscheduled} testid="scout-calendar-unscheduled" />
          <Group title="Recorded" list={past} testid="scout-calendar-past" />
        </>
      )}
    </section>
  );
};

export default EpisodeCalendar;
